import { FilterAnd, FilterEqual, FilterOr } from '@sitecore-search/data';
import type { SearchCriteria } from '@sitecorecomponents/RealEstate/ResidencesByPropertySearch/searchCriteria';
import type { Range, SearchAgent, SearchAgentSettings } from './searchAgentApi';

/* Search index attribute -> agent filter. Rent is rent alone on both sides:
   antenna is an extra on the advert, never part of the filter. */
const RANGE_FIELDS = {
    price: 'rentPrice',
    size_area: 'area',
    size_rooms: 'rooms',
    floor: 'floor',
    year_of_construction: 'constructionYear',
} as const;
type RangeAttribute = keyof typeof RANGE_FIELDS;
const INTEGER_FIELDS = new Set(['rooms', 'floor', 'constructionYear']);

/* The sliders' scale for the attributes an agent can store: an agent waits for
   adverts that don't exist yet, so it can't be held to the values today's
   adverts have. A thumb left on either end means no limit that way - in the
   search filter (buildRangeFilter) as in the agent. */
export const AGENT_SCALES: Record<RangeAttribute, [number, number]> = {
    price: [0, 75000],
    size_area: [0, 300],
    size_rooms: [1, 12],
    floor: [0, 20],
    year_of_construction: [1800, new Date().getFullYear()],
};

/* Area, postcode and property: the location filters an agent stores, applied
   to the search as well. They travel in the URL and in SearchCriteria.values
   like a facet, under these names - the advert index attribute and the Facet
   Attribute Name their facet configurations are authored with. The values are
   what the agent stores: area id, postcode, property number (property_id). */
export const AGENT_VALUE_FILTERS = ['postal_code_area_id', 'postal_code', 'property_id'] as const;
export type AgentValueFilter = typeof AGENT_VALUE_FILTERS[number];

/** Display names per agent-only filter, by value (area id, postcode, property number). */
export type AgentFilterNames = Partial<Record<AgentValueFilter, Record<string, string>>>;

export const isRangeAttribute = (attribute: string): attribute is RangeAttribute => attribute in RANGE_FIELDS;
export const isAgentValueFilter = (attribute: string): attribute is AgentValueFilter =>
    (AGENT_VALUE_FILTERS as readonly string[]).includes(attribute);

/* Keeps what an agent can store - the search's other filters (energy marking,
   facilities, ...) are dropped - and opens range ends left on the scale. */
export function normalizeCriteria(criteria: SearchCriteria): SearchCriteria {
    const result: SearchCriteria = { values: {}, ranges: {} };

    for (const [attribute, [from, to]] of Object.entries(criteria.ranges)) {
        if (!isRangeAttribute(attribute)) continue;
        const [min, max] = AGENT_SCALES[attribute];
        const range: [number | null, number | null] = [from !== null && from > min ? from : null, to !== null && to < max ? to : null];
        if (range[0] !== null || range[1] !== null) result.ranges[attribute] = range;
    }

    for (const [attribute, values] of Object.entries(criteria.values)) {
        if (isAgentValueFilter(attribute) && values.length > 0) result.values[attribute] = values;
    }

    return result;
}

export function criteriaToSettings(criteria: SearchCriteria, name: string): SearchAgentSettings {
    const settings: SearchAgentSettings = { name: name.trim() || null };

    for (const [attribute, [from, to]] of Object.entries(criteria.ranges)) {
        if (!isRangeAttribute(attribute)) continue;
        const field = RANGE_FIELDS[attribute];
        const round = (n: number | null) => (n !== null && INTEGER_FIELDS.has(field) ? Math.round(n) : n);
        settings[field] = { minimum: round(from), maximum: round(to) };
    }

    const { postal_code: postcodes = [], postal_code_area_id: areas = [], property_id: properties = [] } = criteria.values;
    if (postcodes.length > 0 || areas.length > 0) {
        settings.postalCodes = { postalCodes: postcodes, postalCodeAreaIds: areas };
    }
    if (properties.length > 0) settings.properties = { propertyNumbers: properties };

    return settings;
}

export function agentToCriteria(agent: SearchAgent): SearchCriteria {
    const criteria: SearchCriteria = { values: {}, ranges: {} };

    for (const [attribute, field] of Object.entries(RANGE_FIELDS)) {
        const range: Range | null = agent[field];
        if (range && (range.minimum !== null || range.maximum !== null)) {
            criteria.ranges[attribute] = [range.minimum, range.maximum];
        }
    }

    const values: Record<AgentValueFilter, string[]> = {
        postal_code_area_id: (agent.postalCodes?.postalCodeAreas ?? []).map(a => a.id),
        postal_code: (agent.postalCodes?.postalCodes ?? []).map(p => p.code),
        property_id: (agent.properties?.properties ?? []).map(p => p.propertyNumber),
    };
    for (const filter of AGENT_VALUE_FILTERS) {
        if (values[filter].length > 0) criteria.values[filter] = values[filter];
    }

    return criteria;
}

/** The names the agent was read back with, for its chips. */
export function agentFilterNames(agent: SearchAgent): AgentFilterNames {
    return {
        postal_code_area_id: Object.fromEntries((agent.postalCodes?.postalCodeAreas ?? []).map(a => [a.id, a.name])),
        postal_code: Object.fromEntries((agent.postalCodes?.postalCodes ?? []).map(p => [p.code, `${p.code} ${p.city}`])),
        property_id: Object.fromEntries((agent.properties?.properties ?? []).map(p => [p.propertyNumber, p.propertyName || p.propertyNumber])),
    };
}

/* The location filters as a search filter: an OR of equals per attribute, the
   attributes ANDed. */
export function agentValueFilter(values: Partial<Record<AgentValueFilter, string[]>>): FilterAnd | null {
    const filters = AGENT_VALUE_FILTERS.flatMap(filter => {
        const selected = values[filter] ?? [];
        return selected.length > 0 ? [new FilterOr(selected.map(value => new FilterEqual(filter, value)))] : [];
    });
    return filters.length > 0 ? new FilterAnd(filters) : null;
}

const UNITS: Record<RangeAttribute, { prefix?: string; suffix?: string }> = {
    price: { suffix: 'kr./md.' },
    size_area: { suffix: 'm²' },
    size_rooms: { suffix: 'vær.' },
    floor: { prefix: 'Etage' },
    year_of_construction: { prefix: 'Opført' },
};

const MAX_NAMES_PER_CHIP = 3;

/** Short Danish chips for the criteria, e.g. "8000 Aarhus C", "3.600–9.000 kr./md.", "2–3 vær.". */
export function summarizeCriteria(criteria: SearchCriteria, names: AgentFilterNames = {}): string[] {
    const chips: string[] = [];

    for (const filter of AGENT_VALUE_FILTERS) {
        const values = criteria.values[filter];
        if (!values?.length) continue;
        const shown = values.slice(0, MAX_NAMES_PER_CHIP).map(v => names[filter]?.[v] ?? v).join(', ');
        chips.push(values.length > MAX_NAMES_PER_CHIP ? `${shown} +${values.length - MAX_NAMES_PER_CHIP}` : shown);
    }

    for (const attribute of Object.keys(RANGE_FIELDS) as RangeAttribute[]) {
        const bounds = criteria.ranges[attribute];
        if (!bounds) continue;
        const { prefix, suffix } = UNITS[attribute];
        // Years read wrong with a thousands separator.
        const format = (n: number) => (attribute === 'year_of_construction' ? String(n) : n.toLocaleString('da-DK'));
        const [from, to] = bounds;
        const span = from !== null && to !== null
            ? (from === to ? format(from) : `${format(from)}–${format(to)}`)
            : from !== null ? `fra ${format(from)}` : `op til ${format(to!)}`;
        chips.push([prefix, span, suffix].filter(Boolean).join(' '));
    }

    return chips;
}
